import classNames from 'classnames';
import React, { useCallback, useState, JSX } from 'react';
import { AccordionItem, type IAccordionItemUIProps } from './AccordionItem';
import {
	IAccordionItem,
	IBaseListAction,
	IMessageCommandMeta,
} from '@enuan/chat__utils';
import { styled } from '@mui/material/styles';
import { xor as _xor } from 'lodash';

export type IAccordionUIProps = Omit<
	JSX.IntrinsicElements['div'],
	'children' | 'onToggle'
> & {
	children: React.ReactElement<IAccordionItemUIProps>[];
	multiple?: boolean;
	defaultOpen?: React.Key[];
	onToggle?: (key: React.Key, ref: HTMLElement, openItems: React.Key[]) => void;
	softHide?: boolean;
};

type IAccordionProps = Omit<IAccordionUIProps, 'children'> & {
	prefix: string;
	command_id: IMessageCommandMeta['id'];
	onAction?: (action: IBaseListAction) => void;
	cards: IAccordionItem[];
};

export const Accordion = styled(
	({
		cards,
		prefix,
		command_id: metadata_id,
		onAction,
		className,
		onToggle,
		multiple,
		defaultOpen = [],
		softHide,
		...props
	}: IAccordionProps) => {
		const [openItems, setOpenItems] = useState<React.Key[]>(defaultOpen);

		const handleToggle = useCallback(
			(key: React.Key, itemRef: HTMLElement, nextOpenItems: React.Key[]) => {
				itemRef.scrollIntoView({
					block: 'center',
					behavior: 'smooth',
				});
				onToggle?.(key, itemRef, nextOpenItems);
			},
			[onToggle],
		);

		const toggleOpen = useCallback(
			(itemKey: React.Key, itemRef: HTMLElement) => {
				let newOpenItems: React.Key[] = [];
				if (multiple) newOpenItems = _xor(openItems, [itemKey]);
				else {
					if ((openItems[0] ?? '') === itemKey) newOpenItems = [];
					else newOpenItems = [itemKey];
				}

				setOpenItems(newOpenItems);
				handleToggle(itemKey, itemRef, newOpenItems);
			},
			[multiple, openItems, handleToggle],
		);

		return (
			<div
				className={classNames('accordion', 'widget--accordion', className)}
				{...props}
			>
				{cards.map((card, i) => (
					<AccordionItem
						key={i}
						itemKey={i}
						card={card}
						openItems={openItems}
						toggleOpen={toggleOpen}
						softHide={softHide}
						prefix={prefix}
						command_id={metadata_id}
						onAction={onAction}
					/>
				))}
			</div>
		);
	},
	{
		label: 'Accordion',
		target: 'Accordion-root',
	},
)({
	margin: '0.5em 0',
	'& .accordion': {
		'& .accordion__divider': {
			marginLeft: 'calc(38px + 8px)',
		},
		'& .accordion__header': {
			paddingTop: 0,
			paddingLeft: 0,
			paddingBottom: 0,
			background: 'var(--message__accordion__header_background)',
			'& img': {
				borderRadius: '0 6px 6px 6px',
				objectFit: 'none',
				objectPosition: 'center',
				width: '38px',
				height: '38px',
			},
			'& main': {
				padding: '0 8px 0 8px',
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				flex: 1,
				'& .title, & .description': {
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					display: 'block',
				},
				'& .title': {
					color: 'var(--message__accordion__title_color)',
					textTransform: 'capitalize',
				},
				'& .description': {
					color: 'var(--message__accordion__description_color)',
					fontFamily: 'Roboto, sans-serif',
					fontSize: 'x-small',
					textTransform: 'capitalize',
				},
			},
		},
		'& .accordion__content': {
			paddingLeft: 'calc(8px + 38px)',
			background: 'var(--message__accordion__content_background)',
			'& .content': {
				'& .content__description': {
					color: 'var(--message__accordion__text_color)',
					fontSize: '1.1em',
					fontFamily: 'Roboto, sans-serif',
					fontWeight: 'lighter',
					marginBottom: '1em',
					'& a': {
						textDecoration: 'underline',
						color: 'var(--message__accordion__text__link_color)',
					},
				},
				'& .content__footer': {
					textAlign: 'right',
					fontWeight: 'bold',
					marginTop: '0.8em',
					display: 'flex',
					flexWrap: 'wrap',
					flexDirection: 'row-reverse',
					'& button': {
						fontSize: '1.3em',
						padding: '0 0 0 0.8em',
						fontFamily: 'Roboto, sans-serif',
						fontWeight: 'bold',
						color: 'var(--message__accordion__text__link_color)',
						whiteSpace: 'nowrap',
						'&:first-child': {
							color: 'var(--accordion__actions__first_color)',
						},
					},
				},
			},
		},
	},
});
